import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvDynamicFormUtils,
  DvFormUtils,
  DvTabBase,
  FormOf,
} from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import VoltageUtils from '@shared/utils/voltage.utils';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AssetsInformationTabService } from './asset-information-tab.service';
import { AssetInformationDto, VisibleToDepartment } from './interfaces';
import { AssetDto } from '@pages/assets-page/components/interfaces';
import { FindUpdateDialogType } from '@pages/assets-page/components/constants';
import { UpdateAssetFindDialogService } from '@pages/assets-page/components/assets-grid/update-asset-find-dialog/update-asset-find-dialog.service';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';
import { HardwareDeviceStatus } from '@shared/constants/hardware-device-status';
import { FindTagsDto } from '@pages/settings-page/hardware-devices-settings/components/find-tags/find-tags-tabs/find-tags-information-tab/interfaces';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';

@Component({
  selector: 'app-asset-information-tab',
  templateUrl: './asset-information-tab.component.html',
  providers: [AssetsInformationTabService],
  styleUrls: ['asset-information-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssetInformationTabComponent
  extends DvTabBase
  implements OnInit, PendingChanges
{
  private api = inject(RestApiService);
  private assetInformationService = inject(AssetsInformationTabService);
  private gridEntityService = inject(SplitterWithGridHelperService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private assetFindUpdateDialogService = inject(UpdateAssetFindDialogService);
  private splitterService = inject(VtSplitterService);

  formGroup: FormGroup<FormOf<AssetInformationDto>>;
  informationElements: DvDynamicFormElement[];
  notificationElements: DvDynamicFormElement[];
  lastUpdatesElements: DvDynamicFormElement[];
  allElements: DvDynamicFormElement[];
  isInactiveFindTextVisible: boolean;
  isSaveInProgress: boolean;
  private voltageValue: number;
  private initialAssetFindValue: number | undefined;
  private findSubscription: Subscription | undefined;
  private hasAssetEdit: boolean;
  private hasAssetEditRestricted: boolean;

  ngOnInit(): void {
    this.setupForm();
    this.handleAccessRestrictions();
    this.listenToDepartmentChange();
    this.listenToNotificationLostTime();
    this.listenToOwnerChanges();
    this.listenToFindChanges();
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> =>
    this.performSave().pipe(tap(() => (this.isSaveInProgress = false)));

  onEntityUpdate(): void {
    this.findSubscription?.unsubscribe();
    if (this.entityId.includes('_')) return;

    this.getAssetInfo(this.entityId).subscribe(res => {
      this.handleAccessRestrictions();
      DvDynamicFormUtils.setFirstData(
        this.allElements.filter(el => el.options.key !== 'VisibleToDepartment'),
        this.formGroup,
        res
      );
      this.formGroup.markAsPristine();
      this.voltageValue = res.Voltage || 0;
      this.lastUpdatesElements =
        this.assetInformationService.lastUpdatesElements(
          this.setInputFieldColor
        );
      this.initialAssetFindValue = res.Serial;

      if (res.FindID) {
        this.setInitialAssetFindIcon(res);
      }
      this.formGroup.controls.VisibleToDepartment?.setValue(
        (
          res.VisibleToDepartment as unknown as
            | VisibleToDepartment[]
            | undefined
        )?.map(visibleDepartment => visibleDepartment.DepartmentID) as number[]
      );
    });
  }

  onEntityCreate(): void {
    this.findSubscription?.unsubscribe();
    this.voltageValue = 0;
    this.lastUpdatesElements = this.assetInformationService.lastUpdatesElements(
      this.setInputFieldColor
    );
    this.formGroup.enable();
    this.formGroup.reset();

    const { controls } = this.formGroup;
    this.findSubscription = controls.FindID?.valueChanges
      .pipe(
        switchMap(findId => {
          return this.api.getInfo<Pick<FindTagsDto, 'DepartmentID'>>(
            Entity.Find,
            String(findId),
            ['DepartmentID']
          );
        }),
        take(1)
      )
      .subscribe(resp => {
        controls.DepartmentID?.setValue(resp.DepartmentID);
        controls.DepartmentID?.markAsDirty();
      });
  }

  onBaseActions(): void {
    if (!this.entityId) return;
    this.isInactiveFindTextVisible = false;
  }

  onSave(): void {
    this.performSave()
      .pipe(tap(() => (this.isSaveInProgress = false)))
      .subscribe();
  }

  performSave(): Observable<boolean> {
    this.isSaveInProgress = true;
    const dirtyValues: Partial<AssetDto> = {
      ...DvFormUtils.getDirtyValues(this.formGroup),
      Personal: this.formGroup.controls.Personal.value,
    };

    const entityId =
      this.entityId === NEW_ENTITY_ROUTE_PARAM ? '0' : this.entityId;

    if (
      this.initialAssetFindValue &&
      dirtyValues.FindID &&
      this.initialAssetFindValue !== dirtyValues.FindID &&
      entityId !== '0'
    ) {
      return this.assetFindUpdateDialogService
        .openAssetFindUpdateDialog(
          FindUpdateDialogType.UpdateDialog,
          this.initialAssetFindValue
        )
        .closed.pipe(
          switchMap(({ source, responseData }) => {
            if (source !== 'confirm') return of(true);
            this.initialAssetFindValue = dirtyValues.FindID;

            return this.upsertAsset(
              entityId,
              dirtyValues,
              this.assetFindUpdateDialogService.getFindUpdateQueryParams(
                responseData
              )
            ).pipe(
              switchMap(() => this.getAssetInfo(this.entityId)),
              tap(data => {
                this.setInitialAssetFindIcon(data);
                this.formGroup.markAsPristine();
              })
            );
          }),
          map(() => true),
          take(1)
        );
    }
    return this.upsertAsset(entityId, dirtyValues);
  }

  private upsertAsset(
    entityId: string,
    dirtyValues: Partial<AssetDto>,
    queryParams?: string
  ): Observable<boolean> {
    return this.api
      .upsert(Entity.Asset, entityId, dirtyValues, queryParams)
      .pipe(
        tap((id: number | undefined) => {
          this.formGroup.markAsPristine();
          this.isInactiveFindTextVisible = false;
          if (id) {
            const context: GridContext | undefined =
              this.splitterService.agGrid.gridOptions?.context;
            if (context?.entity === Entity.Asset) {
              this.gridEntityService.addNewlyCreatedItemToGrid(
                Entity.Asset,
                id
              );
            } else {
              /** redraw grid when new node added on tree view */
              context?.reloadTreeData?.(
                this.splitterService.agGrid.api,
                context,
                String(id)
              );
            }
          } else {
            this.gridEntityService.refreshRowData(entityId);
          }
        }),
        map(() => true)
      );
  }

  private setupForm(): void {
    this.informationElements = this.assetInformationService.informationElements;
    this.notificationElements =
      this.assetInformationService.notificationElements;
    this.lastUpdatesElements = this.assetInformationService.lastUpdatesElements(
      this.setInputFieldColor
    );
    this.allElements = [
      ...this.informationElements,
      ...this.notificationElements,
      ...this.lastUpdatesElements,
    ];

    this.formGroup = DvFormUtils.createFormGroup(this.allElements, true);
  }

  private setInputFieldColor = (): string => {
    return VoltageUtils.setInputFieldColor(this.voltageValue);
  };

  /** on department change selected department has to be added to VisibleToDepartments
   * list */
  private listenToDepartmentChange(): void {
    this.formGroup.controls.DepartmentID?.valueChanges
      .pipe(
        filter(() => !!this.formGroup.controls.DepartmentID?.dirty),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(departmentId => {
        this.setVisibleToDepartmentsItems();
        const { VisibleToDepartment, DepartmentID } = this.formGroup.controls;
        const existingVisibleToDepartments = VisibleToDepartment?.value || [];
        if (departmentId) {
          if (existingVisibleToDepartments.length) {
            if (!existingVisibleToDepartments.includes(departmentId)) {
              VisibleToDepartment?.setValue([
                departmentId,
                ...existingVisibleToDepartments,
              ]);
            }
          } else {
            VisibleToDepartment?.setValue([departmentId]);
          }
          if (DepartmentID?.dirty) {
            VisibleToDepartment?.markAsDirty();
          }
        }
      });
  }

  private setVisibleToDepartmentsItems(): void {
    const visibleToDepartmentsElement = this.allElements.find(
      el => el.options.key === 'VisibleToDepartment'
    ) as DvDropdownClientSideElement;
    const departmentIdElement = this.allElements.find(
      el => el.options.key === 'DepartmentID'
    ) as DvDropdownClientSideElement;

    visibleToDepartmentsElement.options.api?.setItems(
      departmentIdElement.options.api?.getItems() || []
    );
  }

  private listenToNotificationLostTime(): void {
    this.formGroup.controls.NotificationLostTime?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.formGroup.controls.NotificationLostTime?.setValue(
          value ? new Date() : null,
          {
            emitEvent: false,
          }
        );
      });
  }

  private handleAccessRestrictions(): void {
    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.AssetEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.AssetEditRestricted,
      ]),
    ])
      .pipe(take(1))
      .subscribe(([hasAssetEdit, hasEditRestricted]) => {
        this.hasAssetEdit = hasAssetEdit;
        this.hasAssetEditRestricted = hasEditRestricted;
        if (!hasAssetEdit && !hasEditRestricted) {
          this.formGroup.disable();
        }

        if (hasEditRestricted && !hasAssetEdit) {
          const { controls } = this.formGroup;
          controls.Description.disable();
          controls.FindID?.disable();
          controls.UserID.disable();
          controls.DepartmentID?.disable();
          controls.VisibleToDepartment?.disable();
          controls.AssetTypeID?.disable();
          controls.Personal.disable();
          controls.NotificationLostTime?.disable();
        }
      });
  }

  private listenToOwnerChanges(): void {
    const { controls } = this.formGroup;
    controls.UserID.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(userId => {
        if (!userId) {
          controls.Personal.setValue(false);
          controls.Personal.disable();
        } else {
          controls.Personal.enable();
        }
      });
  }

  private listenToFindChanges(): void {
    const { FindID } = this.formGroup.controls;
    FindID?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        const { NotificationLostTime, NotificationLocationLeaving } =
          this.formGroup.controls;
        if (!id) {
          NotificationLostTime?.disable();
          NotificationLocationLeaving.disable();
        } else if (!this.formGroup.disabled) {
          if (this.hasAssetEdit) {
            NotificationLostTime?.enable();
            NotificationLocationLeaving.enable();
          }

          if (!this.hasAssetEdit && this.hasAssetEditRestricted) {
            NotificationLocationLeaving.enable();
          }
        }

        if (FindID.dirty) {
          const findIDElement = this.allElements.find(
            el => el.options.key === 'FindID'
          )! as DvDropdownClientSideElement;
          const findDropdownItem = findIDElement.options.api
            ?.getItems()
            .find(item => item.FindID === id);
          this.isInactiveFindTextVisible =
            findDropdownItem?.Active === HardwareDeviceStatus.Inactive;
        }
      });
  }

  private getAssetInfo(id: string): Observable<AssetInformationDto> {
    const requestKeys = DvDynamicFormUtils.getKeysForRequest(
      this.allElements
    ).filter(field => field !== 'DepartmentDescription');

    return this.api
      .getInfo<AssetInformationDto>(Entity.Asset, id, requestKeys)
      .pipe(map(resp => ({ ...resp, DepartmentDescription: resp.Department })));
  }

  private setInitialAssetFindIcon(data: AssetInformationDto): void {
    (
      this.allElements.find(
        el => el.options.key === 'FindID'
      ) as DvDropdownClientSideElement
    ).options.items = [
      this.assetInformationService.getFindItemWithIcon({
        FindID: data.FindID,
        Serial: String(data.Serial),
        Active: data.FindActive!,
      }),
    ];
  }
}
